import usePurchase from 'hooks/store/usePurchase';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

/**
 * Visibility requires both:
 * - SHOW_TRAVEL_TIPS_PURCHASE_COMPLETE (feature flag) = true
 * - Purchase origin/destination in env.travelTipsAllowedRoutes (default env). Comparison: case-insensitive, trimmed.
 */

/** Normalizes origin and destination to a comparable key (trimmed, lowercased). */
const toKey = (origin, destination) =>
  `${String(origin || '')
    .trim()
    .toLowerCase()}-${String(destination || '')
    .trim()
    .toLowerCase()}`;

/**
 * True if origin–destination is in the allowed list.
 * @param {string} origin
 * @param {string} destination
 * @param {{ origin: string, destination: string }[]} allowedRoutes
 */
export function isRouteAllowed(origin, destination, allowedRoutes) {
  if (!origin || !destination) return false;
  if (!Array.isArray(allowedRoutes) || allowedRoutes.length === 0) return false;
  const allowed = new Set(allowedRoutes.map((r) => toKey(r?.origin, r?.destination)));
  return allowed.has(toKey(origin, destination));
}

/**
 * True only when SHOW_TRAVEL_TIPS_PURCHASE_COMPLETE is on and purchase route is in env.travelTipsAllowedRoutes.
 */
function useShowTravelTips() {
  const { SHOW_TRAVEL_TIPS_PURCHASE_COMPLETE: showTravelTipsEnabled = false } =
    useWhitelabelFeatures();
  const { travelTipsAllowedRoutes = [] } = useWhitelabelEnvs();
  const purchase = usePurchase();

  if (!showTravelTipsEnabled) return false;

  const departsOk = isRouteAllowed(
    purchase.departs?.origin?.cityName,
    purchase.departs?.destination?.cityName,
    travelTipsAllowedRoutes,
  );
  const returnsOk = isRouteAllowed(
    purchase.returns?.origin?.cityName,
    purchase.returns?.destination?.cityName,
    travelTipsAllowedRoutes,
  );
  return departsOk || returnsOk;
}

export default useShowTravelTips;
